/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Lightbulb, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from './ui/button';

interface TheoryCardProps {
  onDismiss?: () => void;
}

const TheoryCard: React.FC<TheoryCardProps> = ({ onDismiss }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card className="bg-gradient-to-r from-amber-50/90 to-orange-50/90 dark:from-amber-950/40 dark:to-orange-950/40 border-amber-200 dark:border-amber-800 shadow-md">
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <Lightbulb className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h4 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
                Why Visualizing Patterns Matters
              </h4>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="h-6 w-6 p-0"
                >
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
                {onDismiss && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onDismiss}
                    className="h-6 w-6 p-0 ml-4"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mb-2">
              The guitar fretboard can feel overwhelming—264 possible notes on a 22-fret guitar!
              But music theory reveals that it's really just repeating patterns.
              When you see these patterns visually, complex music theory becomes intuitive.
            </p>
            {isExpanded && (
              <div className="space-y-2 pt-2 border-t border-amber-200 dark:border-amber-800">
                <div>
                  <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                    The Professional Secret:
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                    Great guitarists don't memorize hundreds of positions. They recognize moveable shapes and
                    interval relationships. See how the same color pattern appears in multiple places?
                    That's the insight that transforms beginners into confident players who can play anywhere on the neck.
                  </p>
                </div>
                <div>
                  <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                    From Memorization to Understanding:
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                    Instead of memorizing "play these frets," you'll learn "the fifth is always two frets higher on the next string (except G to B strings)"
                    or "this shape moves to any key." This understanding means you can figure out any scale, chord, or solo on the fly.
                    That's musical freedom.
                  </p>
                </div>
                <div>
                  <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                    Scales, Chords, and Fingerings:
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                    Switch between Scale Mode and Chord Mode to see how they connect. Every chord fingering is a moveable shape,
                    and every scale reveals the notes you can improvise with. Understanding both unlocks the fretboard.
                  </p>
                </div>
                <div>
                  <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                    Use the Harmonic Companion:
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                    Click the chat icon to ask our AI about scales, chord progressions, theory concepts, and musical context.
                    Get personalized insights about what you're practicing right now.
                  </p>
                </div>
                <div>
                  <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                    Active Learning:
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                    Don't just stare at the colors—pick up your guitar and play along!
                    Start with the root notes, then add one interval at a time.
                    Change the key and watch the whole pattern shift together.
                    This is how theory becomes muscle memory.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TheoryCard;
